import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "@/lib/apiClient";
import { Plus } from "lucide-react";

type CustomerRow = {
  id?: string;
  name?: string;
  email?: string | null;
  phone?: string | null;
  companyName?: string | null;
};

type CustomersResponse = {
  customers?: CustomerRow[];
  total?: number;
};

export function FinanceCustomersPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customers, setCustomers] = useState<CustomerRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const res = await apiGet<CustomersResponse>("/api/finance/customers?limit=100");
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error || "Could not load customers.");
        setCustomers([]);
      } else {
        setError(null);
        setCustomers(res.data?.customers ?? []);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-6 p-6 sm:p-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Customers</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Directory from <code className="text-sm">/api/finance/customers</code>.
          </p>
        </div>
        <Link
          to="/finance/customers/new"
          className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500"
        >
          <Plus className="h-4 w-4" aria-hidden />
          New customer
        </Link>
      </div>

      {loading ? <p className="text-sm text-slate-500">Loading customers…</p> : null}
      {error ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">{error}</div>
      ) : null}

      {!loading && !error ? (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-950/40">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500 dark:border-white/10 dark:bg-slate-900/50">
              <tr>
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Company</th>
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">Phone</th>
              </tr>
            </thead>
            <tbody>
              {customers.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                    No customers yet for this org.
                  </td>
                </tr>
              ) : (
                customers.map((c) => (
                  <tr key={c.id || c.name} className="border-b border-slate-100 dark:border-white/5">
                    <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">
                      {c.id ? (
                        <Link
                          to={`/finance/customers/${encodeURIComponent(c.id)}`}
                          className="text-indigo-600 hover:underline dark:text-indigo-300"
                        >
                          {c.name?.trim() || c.id}
                        </Link>
                      ) : (
                        c.name?.trim() || "—"
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{c.companyName?.trim() || "—"}</td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{c.email?.trim() || "—"}</td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{c.phone?.trim() || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
